import React from "react"
import { withFormik } from "formik"
import { string, object, ref } from "yup"

import styles from "./SignUpForms.module.scss"
import Input from "../../components/Input/Input"
import PasswordInput from "../../components/PasswordInput/PasswordInput"
import Dropdown from "../../components/Dropdown/Dropdown"
import { teamSize } from "../../helpers/helpData"
import { isHaveBadWords } from "../../helpers/badWords"

const CompanyStep3Form = ({
  values,
  errors,
  handleSubmit,
  handleChange,
  handleBlur,
  touched,
  isSubmitting,
}) => {
  const teamSizeValue = teamSize
    ? teamSize.find((option) => option.value === values.teamSize.value)
    : ""

  return (
    <form className={styles.form} onSubmit={handleSubmit} id="formikForm">
      <div className={styles.formFields}>
        <Input
          name="companyName"
          className={styles.field}
          placeholder="Type your company name"
          label="Company name"
          value={values.companyName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.companyName && touched.companyName}
          errorMessage={
            errors.companyName && touched.companyName && errors.companyName
          }
          disabled={true}
        />
        <Dropdown
          label="Team size"
          placeholder="Select team size"
          wrapperStyles={{ paddingTop: "5px" }}
          name="teamSize"
          border={true}
          options={teamSize || ""}
          value={teamSizeValue}
          className={styles.dropdownField}
          onChange={(name, option) =>
            handleChange({ target: { name: name, value: option } })
          }
          onBlur={() => handleBlur({ target: { name: "teamSize" } })}
          error={errors.teamSize && touched.teamSize}
          errorMessage={
            errors.teamSize && touched.teamSize && errors.teamSize
          }
        />
        <Input
          name="fullName"
          className={styles.field}
          placeholder="Type your full name"
          label="Full name"
          value={values.fullName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.fullName && touched.fullName}
          errorMessage={
            errors.fullName && touched.fullName && errors.fullName
          }
          disabled={true}
        />
        <Input
          name="email"
          type="email"
          className={styles.field}
          placeholder="Type your email"
          label="Email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.email && touched.email}
          errorMessage={errors.email && touched.email && errors.email}
          disabled={true}
        />
        <PasswordInput
          name="password"
          className={styles.passField}
          label="Password"
          placeholder="Type your password"
          value={values.password}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.password && touched.password}
          errorMessage={
            errors.password && touched.password && errors.password
          }
        />
        <PasswordInput
          name="confirmPassword"
          className={styles.passField}
          label="Confirm password"
          placeholder="Confirm your password"
          value={values.confirmPassword}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.confirmPassword && touched.confirmPassword}
          errorMessage={
            errors.confirmPassword &&
            touched.confirmPassword &&
            errors.confirmPassword
          }
        />
      </div>
      <button disabled={isSubmitting} style={{ display: "none" }} type="submit">
        Submit form
      </button>
    </form>
  )
}

const FormikApp = withFormik({
  mapPropsToValues(props) {
    return {
      fullName: props.initialValues?.fullName || "",
      companyName: props.initialValues?.companyName || "",
      email: props.initialValues?.email || "",
      password: "",
      confirmPassword: "",
      teamSize: "",
    }
  },
  validationSchema: () =>
    object().shape({
      teamSize: string().required("Please select team size"),
      fullName: string()
        .strict(false)
        .trim("Please, delete all spaces at the beginning and the end")
        .matches(
          /^[a-zA-Z\s_\-_'_"]*$/,
          "Enter a correct Full Name that doesn't contain any special characters or numbers",
        )
        .matches(/[^\\s*]+/, "Please enter full name")
        .min(
          2,
          "Please enter a correct full name that consists of a minimum 2 characters",
        )
        .max(
          32,
          "Please enter a correct full name that consists of no more than 32 characters",
        )
        .test({
          name: "description_obj",
          test: (val) => {
            if (!val) return true
            if (val) {
              return !isHaveBadWords(val)
            }
          },
          message: `Please don't use bad language`,
        })
        .required("Please enter full name"),
      companyName: string()
        .strict(false)
        .trim("Please, delete all spaces at the beginning and the end")
        .min(
          2,
          "Please enter a correct company name that consists of a minimum 2 characters",
        )
        .max(
          32,
          "Please enter a correct company name that consists of no more than 32 characters",
        )
        .test({
          name: "description_obj",
          test: (val) => {
            if (!val) return true
            if (val) {
              return !isHaveBadWords(val)
            }
          },
          message: `Please don't use bad language`,
        })
        .required("Please enter company name"),
      email: string()
        .email("Please enter your email address in format yourname@example.com")
        .max(
          64,
          "Please enter a valid email address that consists of no more than 64 characters",
        )
        .required("Please enter email address"),
      password: string()
        .min(
          8,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*[a-z])/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*[A-Z])/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*\d)/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .matches(
          /(?=.*\W|_)/,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .max(
          32,
          "Enter a combination of at least 8 characters, no more then 32 characters, with a mix of uppercase letters, lower case letters, numbers & symbols",
        )
        .required("Please enter a password"),
      confirmPassword: string()
        .oneOf(
          [ref("password"), null],
          "Entered passwords didn`t match. Try again.",
        )
        .required("Please confirm your password"),
    }),
  handleSubmit(
    values,
    { setSubmitting, setFieldError, props: { handleSaveUser } },
  ) {
    setSubmitting(true)

    handleSaveUser(values).catch((error) => {
      if (
        error.response.status === 422 &&
        error.response.data.error.toLowerCase().includes("email")
      ) {
        setFieldError(
          "email",
          "That email address is already taken. Try another.",
        )
        setSubmitting(false)
      }
    })
  },
})(CompanyStep3Form)

export default FormikApp
